import React, { useEffect, useMemo } from 'react';
import { VStack } from '@podiumhq/design-system';

import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import {
  getGooglePlacesDetails,
  sendDatadogError,
  splitGmbAddress,
} from 'lib/googleAutocomplete';

import { formatBusinessCountryCode } from './index';
import {
  GoogleAutocompletePrediction,
  GoogleAutocompleteWrapper,
  useGoogleAutocomplete,
} from '../GoogleAutocomplete';

const GoogleAddressSearchResults = ({
  idPrefix = 'Trial',
  listRef,
  getOnKeyDown,
  setShouldHideResults,
  shouldHideResults,
  ...wrapperProps
}) => {
  const formatAddressResults = (prediction) => ({
    description: prediction?.description,
    key: prediction?.place_id,
  });
  const hideResults = () => setShouldHideResults(true);

  const {
    businessInfo = {},
    resetErrors,
    setBusinessInfo,
  } = useBusinessInfoContext() || {};
  const { address: businessAddress, gmbAddress } = businessInfo;

  const {
    clearPredictions,
    getAutocompletePredictions,
    hasNoResultsRef,
    placePredictions,
  } = useGoogleAutocomplete({
    resultsFormatting: formatAddressResults,
    searchType: ['address'],
  });

  const selectGoogleAddress = async ({ description, key }) => {
    if (description?.length > 0) {
      try {
        const [address, city, state, country] = splitGmbAddress(description);
        const googlePlaceDetails = await getGooglePlacesDetails(key);

        setBusinessInfo({
          ...businessInfo,
          address,
          city,
          country: formatBusinessCountryCode(country),
          state: state.toUpperCase(),
          zip: googlePlaceDetails?.zipCode,
          gmbAddress: address,
        });
        resetErrors();
        setShouldHideResults(true);
      } catch (error) {
        sendDatadogError(error, 'getAddressPlacesDetails');
      }
    }
  };

  const handlePredictionSelection = (prediction) =>
    selectGoogleAddress(prediction).then(hideResults);

  useEffect(() => {
    if (businessAddress?.length < 2) {
      clearPredictions();
      hideResults();
    } else if (gmbAddress !== businessAddress) {
      getAutocompletePredictions(businessAddress);
      shouldHideResults && setShouldHideResults(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessAddress]);

  const shouldShowPredictions = useMemo(
    () =>
      !shouldHideResults &&
      businessAddress?.length >= 2 &&
      !hasNoResultsRef.current,
    [businessAddress, shouldHideResults, hasNoResultsRef]
  );

  useEffect(() => {
    setShouldHideResults(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    shouldShowPredictions && (
      <GoogleAutocompleteWrapper
        hideResults={hideResults}
        id={`${idPrefix}-business-address-wrapper`}
        {...wrapperProps}
      >
        {placePredictions?.length > 0 && (
          <VStack align="flex-start" role="listbox" ref={listRef}>
            {placePredictions.map((prediction) => (
              <GoogleAutocompletePrediction
                key={prediction.key}
                description={prediction?.description}
                handlePredictionSelection={handlePredictionSelection}
                onKeyDown={getOnKeyDown}
                prediction={prediction}
              />
            ))}
          </VStack>
        )}
      </GoogleAutocompleteWrapper>
    )
  );
};

export default GoogleAddressSearchResults;
